import { useState } from "react";
import { Clock, CreditCard } from "lucide-react";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { useSubscription } from "@/shared/contexts/SubscriptionContext";

interface SubscriptionBannerProps {
  onUpgrade?: () => void;
}

/**
 * Editorial subscription banner. Hairline border, no gradients. Single
 * accent-colored CTA. Urgency communicated via prose, not color theatrics.
 */
export function SubscriptionBanner({ onUpgrade }: SubscriptionBannerProps) {
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  const {
    subscription,
    isTrialing,
    daysRemaining,
    hasAccess,
    setShowPaywall,
    openBillingPortal,
    paywallEnabled,
  } = useSubscription();

  if (!paywallEnabled) return null;
  if (!subscription) return null;

  if (subscription.status === "active" && !subscription.cancel_at_period_end) {
    return null;
  }

  const handleUpgrade = () => {
    if (onUpgrade) onUpgrade();
    else setShowPaywall(true);
  };

  const handleManageSubscription = async () => {
    try {
      setError(null);
      const url = await openBillingPortal();
      if (url) window.location.href = url;
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Billing settings could not be opened.",
      );
    }
  };

  // Shared shell for all three states.
  const Shell = ({
    icon,
    title,
    subtitle,
    ctaLabel,
    onClick,
  }: {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    ctaLabel: string;
    onClick: () => void;
  }) => (
    <div className="flex flex-wrap items-center justify-start gap-4 px-6 py-3 border-b border-[var(--border)]">
      <div className="flex items-center gap-3 min-w-0">
        <div className="shrink-0 text-[var(--muted)]">{icon}</div>
        <div className="min-w-0">
          <p className="text-sm text-[var(--fg)] leading-tight">{title}</p>
          <p className="text-xs text-[var(--muted)] leading-tight mt-0.5">
            {subtitle}
          </p>
        </div>
      </div>
      <button
        onClick={onClick}
        className="min-h-11 shrink-0 px-4 py-1.5 text-sm font-medium text-[var(--bg)] bg-[var(--accent)] hover:opacity-90 transition-opacity cursor-pointer rounded-[var(--radius)]"
      >
        {ctaLabel}
      </button>
      {error && (
        <p role="alert" className="w-full text-sm text-[var(--mark-attention)]">
          {error}
        </p>
      )}
    </div>
  );

  // Trial banner
  if (isTrialing && hasAccess) {
    return (
      <Shell
        icon={<Clock size={16} strokeWidth={1.75} />}
        title={
          daysRemaining === 1
            ? t.subscription.trialLastDay
            : t.subscription.trialDaysLeft.replace(
                "{days}",
                String(daysRemaining),
              )
        }
        subtitle={t.subscription.trialBannerSubtitle}
        ctaLabel={t.subscription.upgradeCta}
        onClick={handleUpgrade}
      />
    );
  }

  // Canceled but still active
  if (
    hasAccess &&
    (subscription.status === "canceled" || subscription.cancel_at_period_end)
  ) {
    return (
      <Shell
        icon={<CreditCard size={16} strokeWidth={1.75} />}
        title={t.subscription.subscriptionEnding}
        subtitle={
          daysRemaining > 0
            ? t.subscription.accessUntil.replace(
                "{days}",
                String(daysRemaining),
              )
            : t.subscription.accessExpired
        }
        ctaLabel={t.subscription.reactivate}
        onClick={handleManageSubscription}
      />
    );
  }

  if (subscription.status === "past_due" || subscription.status === "unpaid") {
    return (
      <Shell
        icon={<CreditCard size={16} />}
        title="Payment needs attention"
        subtitle="Update your payment method to continue. Your saved work is still available."
        ctaLabel="Manage billing"
        onClick={() => void handleManageSubscription()}
      />
    );
  }

  // Expired
  if (!hasAccess) {
    return (
      <Shell
        icon={<Clock size={16} strokeWidth={1.75} />}
        title={t.subscription.trialExpired}
        subtitle={t.subscription.subscribeToAccess}
        ctaLabel={t.subscription.subscribe}
        onClick={handleUpgrade}
      />
    );
  }

  return null;
}
